<template>
    <div
        ref="modal"
        :id="modalId"
        class="modal fade quote-checkout-iframe-modal"
        tabindex="-1"
        role="dialog"
        aria-hidden="true"
        data-backdrop="false"
    >
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
            <div class="modal-content">
                <div class="modal-header border-0 pb-0">
                    <button
                        type="button"
                        class="close"
                        data-dismiss="modal"
                        aria-label="Stäng"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body p-0">
                    <quote-checkout-iframe
                        v-if="mounted"
                        ref="iframe"
                        :quote-key="quoteKey"
                        :locale="locale"
                        :selected-product-id="selectedProductId"
                        :explicit-product-selection="explicitProductSelection"
                        :selected-kind="selectedKind"
                        :pending-signature="pendingSignature"
                        :pending-order-ref="pendingOrderRef"
                        :pending-ais-resume="pendingAisResume"
                        @complete="onComplete"
                        @signed="onSigned"
                        @cancel="onCancel"
                        @fatal="onFatal"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";

const props = defineProps({
    quoteKey: { type: String, required: true },
    modalId: { type: String, default: "bankid" },
    locale: { type: String, default: "sv" },
    selectedProductId: { type: [Number, String], default: null },
    explicitProductSelection: { type: Boolean, default: false },
    selectedKind: {
        type: String,
        default: null,
        validator: (v) => v === null || v === "product" || v === "option",
    },
    // The page opened this modal on a BankID app-switch return whose signing
    // callback may not have landed yet; the checkout shows a "preparing your
    // signature" state and polls (see useAppSwitchReturnUrl.ts in source/).
    pendingSignature: { type: Boolean, default: false },
    // The BankID order the wait watches, so a cancelled order exits it early.
    pendingOrderRef: { type: String, default: null },
    // The page opened this modal on a Brite AIS app-switch return; the checkout
    // resumes the bank-details assist where the customer left off.
    pendingAisResume: { type: Boolean, default: false },
});

const emit = defineEmits(["complete", "fatal", "closed"]);

const modal = ref(null);
const iframe = ref(null);
const mounted = ref(false);
const completed = ref(false);

function onShow() {
    mounted.value = true;
    completed.value = false;

    document.dispatchEvent(new CustomEvent("quote-checkout-iframe-open"));

    // The checkout renders its own full-screen overlay (portaled onto
    // <body>). Dismiss the modal we were launched from and clear any
    // Bootstrap backdrop it left mid-transition, so neither lingers
    // behind the checkout. This modal carries data-backdrop="false",
    // so any .modal-backdrop in the DOM here is an orphan we own none of.
    const $ = window.jQuery;
    $(".modal.show").not(modal.value).modal("hide");
    $(".modal-backdrop").remove();
}

function onHidden() {
    mounted.value = false;
    emit("closed");
    if (completed.value) {
        window.location.reload();
    }
}

function hide() {
    window.jQuery(modal.value).modal("hide");
}

function onComplete(payload) {
    completed.value = true;
    emit("complete", payload);
}

// The pending wait resolved: the checkout confirmed the quote got signed. The
// page behind this modal was rendered unsigned, so closing must reload it the
// same way a completed in-session checkout does. `complete` deliberately does
// not fire on this path (a read-only reopen must not reload-loop), which is
// why the signed envelope drives it instead.
function onSigned() {
    completed.value = true;
}

function onCancel() {
    hide();
}

function onFatal(payload) {
    emit("fatal", payload);
}

onMounted(() => {
    const $modal = window.jQuery(modal.value);
    $modal.on("show.bs.modal", onShow);
    $modal.on("hidden.bs.modal", onHidden);
});

onBeforeUnmount(() => {
    const $modal = window.jQuery(modal.value);
    $modal.off("show.bs.modal", onShow);
    $modal.off("hidden.bs.modal", onHidden);
});
</script>

<style lang="scss" scoped>
// The real checkout UI portals onto <body> as its own full-overlay modal
// (see quoteCheckoutIframe.vue). This Bootstrap modal is only a lifecycle host
// — mounting/unmounting the iframe on show/hide — so keep its chrome invisible.
// Otherwise its empty dialog card (and Bootstrap's dark backdrop, disabled via
// data-backdrop="false") shows as a ghost modal behind the checkout overlay.
.quote-checkout-iframe-modal {
    .modal-dialog {
        max-width: 560px;
    }

    .modal-header {
        display: none;
    }

    .modal-content {
        background: transparent;
        border: 0;
        box-shadow: none;
    }

    .modal-body {
        min-height: 0;
        padding: 0;
    }
}
</style>
